import * as React from "react";
import jwtDecode from "jwt-decode";
import { useAsync } from "utils/hooks";
import { deleteApiToken, getApiToken, setApiToken } from "utils/storage";
import { useSearchParams } from "react-router-dom";
import { postWalletAuth } from "../api/postWalletAuth";
import { signRequestToVerifyIdentity } from "../api/signRequestToVerifyIdentity";
import type { User, DecodedJWT, Connector, ConnectorState } from "../types";
import { connectorReducer, initialState } from "../reducer";
import { MetaMaskConnector } from "../utils/metaMask";
import { CoinBaseConnector } from "../utils/coinBase";
import { WalletConnectConnector } from "../utils/walletConnect";
import { getAuthRefresh } from "../api/getAuthRefresh";

type SignInFunction = (connector: Connector, address: string) => void;
type RefreshAuthFunction = (updates?: { user: User; jwt: string }) => void;

/**
 * Context to provide all state and actions related to authentication
 */
const AuthContext = React.createContext<
  | {
      // Platform access
      signIn: SignInFunction;
      signOut: VoidFunction;
      refreshAuth: RefreshAuthFunction;
      user: User | null;
      error: null | string;
      isSignedIn: boolean;
      isLoading: boolean;
      isError: boolean;
      // Connectors
      metaMask: { connector: Connector; state: ConnectorState };
      coinBase: { connector: Connector; state: ConnectorState };
      walletConnect: { connector: Connector; state: ConnectorState };
    }
  | undefined
>(undefined);

const decodeToken = (token: string | null): User | null => {
  if (!token) return null;
  try {
    const { user } = jwtDecode<DecodedJWT>(token);
    return user ?? null;
  } catch {
    return null;
  }
};

const decodeAuthError = (error: Error | null) => {
  if (!error) return null;

  const { message } = error;

  if (message.match(/denied message signature/i)) {
    return "The signature request was denied. Please reload this page and try again.";
  }
  if (message.match(/signature/i)) {
    return "The wallet signature verification failed. Please reload this page and try again.";
  }
  // TODO: check API Error message for more info on failed auth call
  if (message.match(/authentication/i)) {
    return "Sorry, it looks like you don't have access. Make sure you connected the correct wallet holding your HUG membership pass.";
  }

  return "Something went wrong. Disconnect from your wallet and try again.";
};

function AuthProvider({ children }: { children: React.ReactNode }) {
  // Get token from URL or Storage on first load
  const [searchParams, setSearchParams] = useSearchParams();
  const urlToken = searchParams.get("token");
  if (urlToken) {
    setApiToken(urlToken);
  }
  const token = urlToken || getApiToken();

  // Remove Token from URL
  React.useEffect(() => {
    if (urlToken) {
      searchParams.delete("token");
      setSearchParams(searchParams);
    }
  }, [searchParams, setSearchParams, urlToken]);

  // Async state for sign in function
  const {
    run,
    data: user,
    isLoading,
    isError,
    error,
  } = useAsync<User>(
    // If token exists, set user data as initial state for sign in function
    token ? { data: decodeToken(token), status: "success" } : {}
  );

  /**
   * Authenticate using wallet address and update Auth context
   */
  const signIn: SignInFunction = React.useCallback(
    async (connector: Connector, walletAddress: string) =>
      // Wrap the user data in a promise to resolve in useAsync's data property
      run(
        // Verify wallet by creating digital signature
        signRequestToVerifyIdentity(connector, walletAddress).then(
          (signature) =>
            // Authenticate address and signature via the API
            postWalletAuth(walletAddress, signature).then(({ jwt }) => {
              // Decode the token to get user data
              const userData = decodeToken(jwt);

              if (!userData) {
                throw new Error("Authentication failed");
              }

              // Store the token response in storage
              setApiToken(jwt);
              return userData;
            })
        )
      ),
    [run]
  );

  /**
   * Refresh the auth context's user value and stored token with new values from given updates
   * or by fetching them from the API
   */
  const refreshAuth: RefreshAuthFunction = React.useCallback(
    async (updates) => {
      let { jwt, user: userData } = updates ?? { user: null };

      if (!jwt) {
        // Authenticate address and signature via the API
        jwt = (await getAuthRefresh())?.jwt;
        // Decode the token to get user data
        userData = decodeToken(jwt);
      }

      if (userData) {
        // Store the token response in storage
        setApiToken(jwt);
        // Wrap the user data in a promise to resolve in useAsync's data property
        run(Promise.resolve(userData));
      }
    },
    [run]
  );

  const signOut: VoidFunction = React.useCallback(() => {
    deleteApiToken();
    // Reload by navigating to home page
    window.location.href = window.location.origin;
  }, []);

  // Initialize each connector with its own reducer dispatch function
  const [metaMaskState, metaMaskDispatch] = React.useReducer(
    connectorReducer,
    initialState
  );
  const metaMaskConnector = React.useMemo(
    () => new MetaMaskConnector(metaMaskDispatch),
    [metaMaskDispatch]
  );
  const [coinBaseState, coinBaseDispatch] = React.useReducer(
    connectorReducer,
    initialState
  );
  const coinBaseConnector = React.useMemo(
    () => new CoinBaseConnector(coinBaseDispatch),
    [coinBaseDispatch]
  );
  const [walletConnectState, walletConnectDispatch] = React.useReducer(
    connectorReducer,
    initialState
  );
  const walletConnectConnector = React.useMemo(
    () => new WalletConnectConnector(walletConnectDispatch),
    [walletConnectDispatch]
  );

  // Memoize constructed value or it will update every render
  const value = React.useMemo(
    () => ({
      signIn,
      signOut,
      refreshAuth,
      user,
      error: decodeAuthError(error),
      isSignedIn: !!user,
      isLoading,
      isError,
      metaMask: {
        connector: metaMaskConnector,
        state: metaMaskState,
      },
      coinBase: {
        connector: coinBaseConnector,
        state: coinBaseState,
      },
      walletConnect: {
        connector: walletConnectConnector,
        state: walletConnectState,
      },
    }),
    [
      signIn,
      signOut,
      refreshAuth,
      user,
      error,
      isLoading,
      isError,
      metaMaskConnector,
      metaMaskState,
      coinBaseConnector,
      coinBaseState,
      walletConnectConnector,
      walletConnectState,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

const useAuth = () => {
  const context = React.useContext(AuthContext);

  if (!context) {
    throw new Error(
      "useAuth() may be used only within the context of a <AuthProvider> component"
    );
  }

  return context;
};

export { AuthProvider, useAuth, decodeToken };
